import type { MyWorkItem } from './api'

// "Next of N" from My work (release 3.3, lane 2, the 7 Oct plan's H2).
//
// # WHY
//
// A lead with eight things waiting opened one from My work, did it, pressed
// Back to My work, found the next row and opened that: two page loads and a
// scan of the list for every task. Now opening a row from My work keeps the
// list it was opened from, and the screen the task is done on shows one line:
// "From My work: 2 of 8", with Previous, Next (named) and Back to the list. The
// list is a snapshot of the rows as they were when the row was opened, so a
// task finished on the way is still where it was and Next never skips one.
//
// # WHERE IT IS KEPT
//
// In sessionStorage: one tab's queue, gone when the tab closes, never shared
// with another tab working another list. A row's own address decides whether
// the bar shows: on any screen that is not one of the queue's tasks, there is
// no bar. The queue carries who started it and when: signing out ends it, a
// different person never sees it, it lapses after two hours, and opening My
// work again starts afresh, so the bar never names an old list's tasks.

export type QueueItem = { key: string; href: string; title: string; kind: string }
export type WorkQueue = { from: string; items: QueueItem[]; at: number; subject: string; startedAt: number }

// How long a queue is offered after it was started.
export const QUEUE_LIFETIME_MS = 2 * 60 * 60 * 1000

const KEY = 'tprm.workQueue'

// The address without its query, so a task's link and the address its screen
// writes back (a tab, a filter, an origin) still match.
export function pathOf(href: string): string {
  return (href.replace(/^#/, '').split('?')[0] ?? '').replace(/\/+$/, '')
}

// The screen an address opens. A vendor's record is one screen whatever tab
// or part its address names: the page writes My work's #/vendor/<id>/full/<key>
// again as the tab's own address (release 3.3, V1), and a reader moves between
// its tabs while doing the task, so the bar must not drop off on Back, Forward,
// a reload or a press on another tab. Sending a questionnaire
// (#/vendor/<id>/invite) is a screen of its own.
export function screenOf(href: string): string {
  const p = pathOf(href)
  const m = /^\/?vendor\/([^/]+)(\/.*)?$/.exec(p)
  if (!m || m[1] === 'new' || /^\/invite(\/|$)/.test(m[2] ?? '')) return p
  return 'vendor/' + m[1]
}

// The rows a queue can step through: the ones that open a screen. A reminder
// does its work in place, and a notice is marked read by its own row, so
// neither is a step (reaching a notice by Next would leave it unread).
export function queueItems(items: MyWorkItem[]): QueueItem[] {
  return items
    .filter((it) => it.action.href && it.action.kind === 'open')
    .map((it) => ({ key: it.key, href: it.action.href, title: it.title, kind: it.kind }))
}

export function startQueue(items: MyWorkItem[], key: string, from: string, subject: string, now = Date.now()): WorkQueue | null {
  const list = queueItems(items)
  const at = list.findIndex((q) => q.key === key)
  if (at < 0 || list.length < 2) {
    endQueue()
    return null
  }
  const q: WorkQueue = { from, items: list, at, subject, startedAt: now }
  try {
    sessionStorage.setItem(KEY, JSON.stringify(q))
  } catch {
    // A browser that refuses storage simply has no bar.
  }
  return q
}

export function readQueue(): WorkQueue | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const q = JSON.parse(raw) as WorkQueue
    return Array.isArray(q.items) && typeof q.at === 'number' && typeof q.from === 'string' && typeof q.subject === 'string'
      ? q
      : null
  } catch {
    return null
  }
}

export function moveQueue(at: number): void {
  const q = readQueue()
  if (!q || at < 0 || at >= q.items.length) return
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...q, at }))
  } catch {
    // Nothing to do: the bar works out where it is from the address.
  }
}

export function endQueue(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // Nothing kept, nothing to remove.
  }
}

// Where the address stands in the queue: the item it is, with the ones either
// side. The remembered position wins when it matches (two rows can open the
// same screen); otherwise the first row whose screen this is.
export function queueHere(
  q: WorkQueue | null,
  hash: string,
  subject: string,
  now = Date.now(),
): { at: number; of: number; prev?: QueueItem; next?: QueueItem; from: string } | null {
  if (!q || q.items.length < 2) return null
  // Somebody else's list, or an old one: no bar.
  if (!subject || q.subject !== subject || now - q.startedAt > QUEUE_LIFETIME_MS) return null
  const here = screenOf(hash)
  const remembered = q.items[q.at]
  const at = remembered && screenOf(remembered.href) === here ? q.at : q.items.findIndex((i) => screenOf(i.href) === here)
  if (at < 0) return null
  return { at, of: q.items.length, prev: q.items[at - 1], next: q.items[at + 1], from: q.from }
}
